import { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';

import { PredefinedMilisecondsTypeEnum } from '../../../lib/enums';
import lang from '../../../lib/language';
import { ChangeIndividualUserNameRequest } from '../../../lib/models/crm/types';
import { RootState } from '../../../lib/store';
import { getIndividualExtendedInfo } from '../../../lib/store/common-actions';
import { changeIndividualUserName, getIndividualUserName, updateIndividualTransitionToForActive } from '../../../lib/store/crm';
import { CRMCall } from '../../../lib/store/crm/types';

import { Preloader } from '../../../components';
import { useIsMobile } from '../../../hooks';
import EnrollmentState from '../../../store-util/EnrollmentState';
import { UI_ROUTES } from '../../../util/routes';
import Layout from '../component/Layout';

import styles from './EnrollSetupUserName.module.scss';

type Props = ReduxProps;

const EnrollSetupUserName = ({
  activateUser,
  isAppLoaded,
  isUserNameUsed,
  tradingPlatformId,
  changeUserName,
  stateData,
  isPhoneConfirmed,
  isEmailConfirmed,
  individualUserName,
  individualId,
  getUserName,
  isProfileNameCreated,
}: Props): JSX.Element => {
  const { form } = EnrollmentState.use();
  const navigate = useNavigate();
  const { isMobile } = useIsMobile();
  const usernameMinChars = 4;
  const usernameMaxChars = 16;

  const [profile_name, setProfileName] = useState('');
  const [usernameErrorText, setusernameErrorText] = useState('');
  const [showError, setShowError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  useEffect(() => {
    // if enroll was cancelled and resumed, set the appropriate steps
    if (isEmailConfirmed) {
      EnrollmentState.setVerifyEmailStepPassed(true);
    }
    if (isPhoneConfirmed) {
      EnrollmentState.setVerifyPhoneStepPassed(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateData, isPhoneConfirmed, isEmailConfirmed]);

  useEffect(() => {
    if (individualId) {
      activateUser();
      getUserName();
    }
  }, [individualId, activateUser, getUserName]);

  useEffect(() => {
    individualUserName && setProfileName(individualUserName);
  }, [individualUserName]);

  // Navigate only after the re-fetched extended info confirms the new username.
  // Leaving earlier races the App guard, which bounces back to the phone screen.
  useEffect(() => {
    if (!hasSubmitted) return;

    if (isUserNameUsed) {
      setShowError(true);
      setusernameErrorText(lang.userNameExist());
      setHasSubmitted(false);
      return;
    }

    if (isProfileNameCreated && isPhoneConfirmed) {
      EnrollmentState.setSetupUserNameStepPassed(true);
      navigate(UI_ROUTES.baseURL, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSubmitted, isUserNameUsed, isProfileNameCreated, isPhoneConfirmed]);

  useEffect(() => {
    if (isAppLoaded && (!form.steps.isVerifyPhonePassed)) {
      navigate(UI_ROUTES.enrollPhoneVerificaion, {
        replace: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAppLoaded, form.steps.isVerifyPhonePassed, tradingPlatformId]);

  const validateUsername = () => {
    if (profile_name.length < usernameMinChars) {
      setusernameErrorText(lang.validationCapitalMinimumSymbols(usernameMinChars));
      setShowError(true);
      return false;
    }
    if (profile_name.length > usernameMaxChars) {
      setusernameErrorText(lang.validationCapitalMaximumSymbols(usernameMaxChars));
      setShowError(true);
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (!validateUsername()) return;
    setShowError(false);
    setHasSubmitted(true);
    changeUserName({ individual_id: individualId as number, profile_name });
  };

  if (isLoading) {
    // This delay is for the give chance for the BE to create trading accounts
    return (
      <div className={styles.loader}>
        <Preloader
          isFullScreen
          text={lang.commonMessageLoading()}
          owner="EnrollSetupUserName"
          timeOutSetup={{
            timeout: PredefinedMilisecondsTypeEnum.twoSeconds,
            checkIfTimeout: () => true,
            onTimeout: () => {
              setIsLoading(false);
            },
          }}
        />
      </div>
    );
  }

  // Loader while the change + confirming re-fetch run, so the form doesn't flash back.
  if (hasSubmitted && !showError) {
    return (
      <div className={styles.loader}>
        <Preloader
          isFullScreen
          text={lang.commonMessageLoading()}
          owner="EnrollSetupUserName"
        />
      </div>
    );
  }

  return (
    <Layout
      formTitle=""
      displayBackButton={!isMobile}
      displayNextButton
      nextButtonText={lang.commonContinueButton()}
      displayLoginButton={false}
      onSubmit={handleSubmit}
    >
      <div className={styles.wrapper}>
        <div className={styles.title}>{`${lang.userNameTitle()}.`}</div>
        <div className={styles.description}>{lang.userNameDescription()}</div>
        <div className={styles.inputWrapper}>
          <div className={styles.inputTitle}>{lang.userNameTitle()}</div>
          <input
            type="text"
            className={classNames(styles.input, showError && styles.inputFail)}
            placeholder={lang.userNameTitle()}
            value={profile_name}
            onChange={(e) => {
              setProfileName(e.target.value);
              setShowError(false);
              setusernameErrorText('');
            }}
          />
          {showError && <div className={styles.inputError}>{usernameErrorText}</div>}
        </div>
      </div>
    </Layout>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  activateUser(): void {
    dispatch(updateIndividualTransitionToForActive());
  },
  changeUserName: (data: ChangeIndividualUserNameRequest) => {
    dispatch(changeIndividualUserName(data));
  },
  getUserName: (): void => dispatch(getIndividualUserName()),
  getExtendedInfo: (): void => {
    dispatch(getIndividualExtendedInfo());
  },
});

const mapStateToProps = (state: RootState) => ({
  isAppLoaded: state.app.loaded,
  tradingPlatformId: state.crm.tradingPlatformId,
  isUserNameUsed: state.crm.statusByCall[CRMCall.changeIndividualUserName]?.errorCode === 409,
  individualId: state.crm.individualId,
  stateData: state,
  individualUserName: state.crm.individualUserName,
  isPhoneConfirmed: state.crm.individualExtendedInfo?.phone_number_confirmed,
  isEmailConfirmed: state.crm.individualExtendedInfo?.email_confirmed,
  isProfileNameCreated: !!state.crm.individualExtendedInfo?.profile_name,
});
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(EnrollSetupUserName);
